import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, Save, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import postService from '../../services/postService';

interface SimplePostEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  post: any;
  onSuccess?: () => void;
}

interface FormData {
  title: string;
  content: string;
}

const SimplePostEditModal: React.FC<SimplePostEditModalProps> = ({
  isOpen,
  onClose,
  post,
  onSuccess
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormData>();

  useEffect(() => {
    if (post && isOpen) {
      setValue('title', post.title || '');
      setValue('content', post.content || '');
    }
  }, [post, isOpen, setValue]);

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      await postService.updatePost(post.id, data);
      toast({ title: "Success", description: "Post updated successfully." });
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update post.", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-black dark:text-white">Edit Post</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
            <X className="w-5 h-5 text-black dark:text-white" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1 text-black dark:text-white">Title</label>
            <input
              {...register('title', { required: 'Title is required' })}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600"
              placeholder="Enter title"
            />
            {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium mb-1 text-black dark:text-white">Content</label>
            <textarea
              {...register('content', { required: 'Content is required' })}
              rows={6}
              className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600 resize-none"
              placeholder="Enter content"
            />
            {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content.message}</p>}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50 dark:hover:bg-gray-700 text-black dark:text-white border-gray-300 dark:border-gray-600"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 flex items-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SimplePostEditModal;